import { useEffect, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { ClipboardList, PlusCircle } from 'lucide-react';
import { useSupabaseClient } from '../hooks/useSupabaseClient';

export default function StudentLayout({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const getSupabase = useSupabaseClient();

  // Öğrenci giriş yaptığında Google/Clerk adını student_snapshots tablosuna otomatik senkronize et
  useEffect(() => {
    async function syncStudentName() {
      if (!user) return;
      const displayName = user.fullName || user.firstName || user.primaryEmailAddress?.emailAddress;
      if (!displayName) return;

      try {
        const supabase = await getSupabase();
        await supabase
          .from('student_snapshots')
          .upsert(
            {
              student_id: user.id,
              display_name: displayName,
              last_active_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'student_id' }
          );
      } catch (err) {
        console.error('Öğrenci profil ismi senkronize edilemedi:', err);
      }
    }
    syncStudentName();
  }, [user, getSupabase]);

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/80 backdrop-blur-xl px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <img src="/logo_miyo.webp" alt="Bitmiyo" className="w-10 h-10 object-contain shrink-0" />
          <span className="font-black text-lg text-text-primary tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Bitmiyo Sınıf
          </span>
        </div>

        {/* Nav */}
        <nav className="flex items-center gap-1">
          <NavLink
            to="/gorevlerim"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                isActive
                  ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500'
                  : 'text-text-secondary hover:bg-border/40 hover:text-text-primary'
              }`
            }
          >
            <ClipboardList size={15} />
            Görevlerim
          </NavLink>
          <NavLink
            to="/katil"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                isActive
                  ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500'
                  : 'text-text-secondary hover:bg-border/40 hover:text-text-primary'
              }`
            }
          >
            <PlusCircle size={15} />
            Sınıfa Katıl
          </NavLink>
          <div className="ml-2 pl-2 border-l border-border">
            <UserButton />
          </div>
        </nav>
      </header>

      <main>{children}</main>
    </div>
  );
}
